// pages/master/vtds/MasterVtdsAddEdit.jsx
'use client';

import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Grid,
  Divider
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import PickerFieldTable from '../../../components/PickerFieldTable';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';

import { createVtds, updateVtds, getVtdsById } from '../../../stores/vtdsStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { useVehicleForGpsList } from '../../../stores/vehicleForGpsStore';
import MasterVehicleAddEdit from '../vehicle/MasterVehicleAddEdit';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import { useLanguage } from '../../../utils/i18n';

// Removed custom filter hooks

// Konstanta Pilihan Dropdown
const VENDOR_OPTIONS = ['TELTONIKA', 'RUPTELA', 'CONCOX', 'ANY GPS'];
const GPS_STATUS_OPTIONS = [
  { value: 'active', key: 'Active' },
  { value: 'repair', key: 'Repair' },
  { value: 'broken', key: 'Broken' },
  { value: 'setting', key: 'Setting' }
];
const DIN_OPTIONS = [
  'DEN',
  'DDR',
  'DPB',
  'DPW',
  'DCO',
  'SPR',
  'SPL',
  'DRR',
  'DRB',
  'DTG',
  'RTR',
  'RTL',
  'Demo',
  'DIM',
  'DUM',
  'NDB',
  'NDL',
  'NDR',
  'NDU'
];
const FUEL_OPTIONS = ['AIN', 'RFID'];
const AIN_OPTIONS = ['AIN1', 'AIN2', 'AIN3', 'AIN4', 'AIN5'];

// Helper Tanggal Hari Ini (YYYY-MM-DD)
const getTodayDate = () => new Date().toISOString().split('T')[0];

// Helper Format Rupiah
const formatRupiah = (value) => {
  if (value == null || value === '') return '';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(value);
};

const MasterVtdsAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company: null,
    company_name: '',
    imei_no: '',
    vehicle: null,
    license_plate: '',
    no_gsm: '',
    gps_vendor: '',
    gps_type: '',
    port: '',
    status_gps: '',

    // Digital Inputs
    din1_desc: '',
    din2_desc: '',
    din3_desc: '',
    din4_desc: '',
    din5_desc: '',

    // Fuel & Sensor
    fuel_desc: '',
    blacklist: false,
    have_ain: false,
    ain: '',
    eco_driver_supported: false,
    buzzer_installed: false,
    stick_fuel_sensor: false,
    canbus_fuel_sensor: false,
    is_ev: false,
    dist_in: 'km',

    // Purchase & Sales
    purchase_form: '',
    purchase_price: '',
    purchase_date: getTodayDate(),
    warranty_date: getTodayDate(),
    selling_price: '',
    selling_date: getTodayDate(),
    insurance_date: getTodayDate(),
    insurance_np: '',

    // Integration
    integration_vendor: '',
    integration_imei: '',
    maximum_allow_data: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  // ---- Fetch Internal Lookups ----
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const companyOptions = companyPickerParams.options;

  useEffect(() => {
    if (id || !open) return;

    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    if (!stored) return;

    if (companyOptions && companyOptions.length) {
      const match = companyOptions.find((c) => String(c.value) === String(stored));
      if (match) {
        setSelectedCompanyId(String(match.value)); // Trigger filter opsi
        setInitialValues((prev) => {
          if (prev.company) return prev;
          return {
            ...prev,
            company: String(match.value),
            company_name: match.label ?? match.name ?? String(match.value)
          };
        });
        return;
      }
    }

    setSelectedCompanyId(String(stored)); // Trigger filter opsi
    setInitialValues((prev) => {
      if (prev.company) return prev;
      return {
        ...prev,
        company: String(stored),
        company_name: String(stored)
      };
    });
  }, [companyOptions, id, open]);

  // Fetch Vehicle Server Side Pagination & Search
  const vehiclePickerParams = usePickerPagination({
    fetchHook: useVehicleForGpsList,
    buildArgs: (state) => {
      const companyQArr = state.selectedCompanyId
        ? [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: state.selectedCompanyId }]
        : [];
      return [state.page, state.pageSize, state.searchKey, state.searchValue, companyQArr];
    },
    searchKey: 'license_plate',
    selectedCompanyId
  });

  // Kolom PickerFieldTable untuk Vehicle
  const vehicleColumns = [
    { field: 'license_plate', headerName: 'License Plate', width: 250, renderCell: (row) => row?.raw?.license_plate ?? row?.label ?? '-' },
    { field: 'status', headerName: 'Status', width: 100, renderCell: (row) => row?.raw?.status ?? '-' }
  ];
  // ---- end lookups ----

  // Fetch data Edit
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getVtdsById(id);
        if (!mounted) return;
        if (data) {
          const companyVal = data.company ?? data.company_id ?? null;
          const vehicleVal = data.vehicle ?? data.vehicle_id ?? null;
          if (companyVal != null) setSelectedCompanyId(String(companyVal));

          // Format ISO date dari API ke YYYY-MM-DD
          const parseDate = (d) => (d ? String(d).split('T')[0] : getTodayDate());

          setInitialValues((prev) => ({
            ...prev,
            company: companyVal != null ? String(companyVal) : prev.company,
            company_name: data.company_name ?? data.join?.company_name ?? prev.company_name,
            imei_no: data.imei_no ?? prev.imei_no,
            vehicle: vehicleVal != null ? String(vehicleVal).split(',') : prev.vehicle,
            license_plate: data.license_plate ?? data.join?.license_plate ?? prev.license_plate,
            no_gsm: data.no_gsm ?? prev.no_gsm,
            gps_vendor: data.gps_vendor ?? prev.gps_vendor,
            gps_type: data.gps_type ?? prev.gps_type,
            port: data.port ?? prev.port,
            status_gps: data.status_gps ?? prev.status_gps,

            din1_desc: data.din1_desc ?? prev.din1_desc,
            din2_desc: data.din2_desc ?? prev.din2_desc,
            din3_desc: data.din3_desc ?? prev.din3_desc,
            din4_desc: data.din4_desc ?? prev.din4_desc,
            din5_desc: data.din5_desc ?? prev.din5_desc,

            fuel_desc: data.fuel_desc ?? prev.fuel_desc,
            blacklist: Boolean(data.blacklist),
            have_ain: Boolean(data.have_ain),
            ain: data.ain ?? prev.ain,
            eco_driver_supported: Boolean(data.eco_driver_supported),
            buzzer_installed: Boolean(data.buzzer_installed),
            stick_fuel_sensor: Boolean(data.stick_fuel_sensor),
            canbus_fuel_sensor: Boolean(data.canbus_fuel_sensor),
            is_ev: Boolean(data.is_ev),
            dist_in: data.dist_in || 'km',

            purchase_form: data.purchase_form ?? prev.purchase_form,
            purchase_price: data.purchase_price ?? prev.purchase_price,
            purchase_date: parseDate(data.purchase_date),
            warranty_date: parseDate(data.warranty_date),
            selling_price: data.selling_price ?? prev.selling_price,
            selling_date: parseDate(data.selling_date),
            insurance_date: parseDate(data.insurance_date),
            insurance_np: data.insurance_np ?? data.insurance_no ?? prev.insurance_np,

            integration_vendor: data.integration_vendor ?? prev.integration_vendor,
            integration_imei: data.integration_imei ?? prev.integration_imei,
            maximum_allow_data: data.maximum_allow_data ?? prev.maximum_allow_data,
            description: data.description ?? prev.description
          }));
        }
      } catch (err) {
        console.error('Failed to load GPS:', err);
        notify({ message: t('masterVtds.failedLoadGps', 'Failed to load GPS data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  // Validasi Formik
  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('masterCompany.companyRequired', 'Company is required')),
    imei_no: Yup.number()
      .typeError(t('general.mustBeNumber', 'Must be a number'))
      .required(t('masterVtds.imeiRequired', 'IMEI is required')), //Harus 15 karakter
    vehicle: Yup.mixed().required(t('masterVtds.vehicleRequired', 'Vehicle is required')),
    no_gsm: Yup.number()
      .typeError(t('general.mustBeNumber', 'Must be a number'))
      .required(t('masterVtds.simcardRequired', 'SIM Card is required')),
    gps_vendor: Yup.string().required(t('masterVtds.gpsVendorRequired', 'GPS Vendor is required')),
    gps_type: Yup.string().required(t('masterVtds.gpsTypeRequired', 'GPS Type is required')),
    port: Yup.number().typeError(t('general.mustBeNumber', 'Must be a number')).required(t('masterVtds.portRequired', 'Port is required')),
    status_gps: Yup.string().required(t('masterVtds.gpsStatusRequired', 'GPS Status is required'))
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          ...values,
          id: id ?? 0,
          is_ev: Boolean(values.is_ev),
          dist_in: String(values.dist_in),
          company: Number(values.company),
          vehicle: Number(values.vehicle),
          imei_no: String(values.imei_no),
          no_gsm: String(values.no_gsm),
          port: String(values.port),
          purchase_price: Number(values.purchase_price),
          selling_price: Number(values.selling_price),
          maximum_allow_data: values.maximum_allow_data ? Number(values.maximum_allow_data) : null
        };

        // Hapus label join dari payload
        delete payload.company_name;
        delete payload.license_plate;

        if (id) {
          await updateVtds(id, payload);
          notify({ message: t('masterVtds.updateSuccess', 'GPS successfully updated'), severity: 'success' });
        } else {
          await createVtds(payload);
          notify({ message: t('masterVtds.createSuccess', 'GPS successfully created'), severity: 'success' });
        }

        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Handler Khusus untuk Input Rupiah
  const handleCurrencyChange = (e, fieldName) => {
    const rawValue = e.target.value.replace(/\D/g, ''); // Buang semua karakter selain angka
    formik.setFieldValue(fieldName, rawValue ? Number(rawValue) : '');
  };

  const dialogActions = (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          backgroundColor: '#0072DA',
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('general.processing', 'Processing...') : t('general.save', 'Save')}
      </Button>
      <Button onClick={onClose} disabled={formik.isSubmitting} variant="outlined">
        {t('general.cancel', 'Cancel')}
      </Button>
    </>
  );

  const SectionHeader = ({ title }) => (
    <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 2, mt: 1 }}>
      {title}
      <Divider sx={{ mt: 1 }} />
    </Typography>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={id ? t('masterVtds.editGps', 'Edit GPS') : t('masterVtds.addGps', 'Add GPS')}
      fullScreenMode={true}
      fullHeight={true}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container justifyContent="space-between" spacing={4}>
              <Grid size={{ sm: 12, md: 6 }}>
                {/* Bagian 1: General Info */}
                <Box sx={{ mb: 4 }}>
                  <SectionHeader title={t('masterVtds.generalDeviceInfo', 'General & Device Info')} />
                  <Grid>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <PickerField
                          label={`${t('masterCompany.company', 'Company')} *`}
                          value={formik.values.company}
                          {...companyPickerParams}
                          options={companyOptions}
                          selectedItems={formik.values.company ? [{ value: formik.values.company, label: formik.values.company_name }] : []}
                          allowAdd
                          AddComponent={MasterCompanyAddEdit}
                          onChange={(opt) => {
                            const val = typeof opt === 'object' ? opt?.value : opt;
                            const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                            const idVal = selected ? selected.value : opt;
                            formik.setFieldValue('company', idVal);
                            setSelectedCompanyId(idVal || '');
                            formik.setFieldValue('vehicle', '');
                            formik.setFieldValue('license_plate', '');
                          }}
                          dialogTitle={t('masterVtds.selectCompany', 'Select Company')}
                          error={formik.touched.company && !!formik.errors.company}
                          helperText={formik.touched.company && formik.errors.company}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <PickerFieldTable
                          label={`${t('masterRole.vehicle', 'Vehicle')} *`}
                          multiSelect={false}
                          value={formik.values.vehicle}
                          {...vehiclePickerParams}
                          options={vehiclePickerParams.options}
                          selectedItems={formik.values.vehicle ? [{ id: formik.values.vehicle, label: formik.values.license_plate }] : []}
                          columns={vehicleColumns}
                          disabled={!selectedCompanyId}
                          onChange={(opt) => {
                            formik.setFieldValue('vehicle', opt);
                            // Cari opsi yang dipilih untuk mendapatkan license_plate
                            const found = vehiclePickerParams.options.find((o) => String(o.id ?? o.value) === String(opt));
                            formik.setFieldValue('license_plate', found ? (found.raw?.license_plate ?? found.label) : '');
                          }}
                          getOptionId={(o) => o?.id ?? o?.value}
                          getOptionLabel={(o) => o?.raw?.license_plate ?? o?.label}
                          dialogTitle={t('masterVtds.selectVehicle', 'Select Vehicle')}
                          error={formik.touched.vehicle && !!formik.errors.vehicle}
                          helperText={
                            (formik.touched.vehicle && formik.errors.vehicle) ||
                            (!selectedCompanyId ? t('masterVtds.selectCompanyFirst', 'Please select a company first') : '')
                          }
                          allowAdd
                          AddComponent={MasterVehicleAddEdit}
                          filterField="status"
                          dialogProps={{ maxWidth: 'sm' }}
                          filterOptions={[
                            { label: 'All', value: 'all' },
                            { label: 'Ready', value: 'ready' },
                            { label: 'Not Ready', value: 'not-ready' }
                          ]}
                          defaultFilterValue="all"
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          label={`${t('masterVtds.simcardNo', 'SIM Card Number')} *`}
                          name="no_gsm"
                          type="number"
                          value={formik.values.no_gsm}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          fullWidth
                          error={formik.touched.no_gsm && !!formik.errors.no_gsm}
                          helperText={formik.touched.no_gsm && formik.errors.no_gsm}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          label={`${t('masterVtds.gpsType', 'GPS Type')} *`}
                          name="gps_type"
                          value={formik.values.gps_type}
                          onChange={formik.handleChange}
                          fullWidth
                          error={formik.touched.gps_type && !!formik.errors.gps_type}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <FormControl fullWidth error={formik.touched.status_gps && !!formik.errors.status_gps}>
                          <InputLabel>{`${t('masterVtds.gpsStatus', 'GPS Status')} *`}</InputLabel>
                          <MuiSelect
                            name="status_gps"
                            value={formik.values.status_gps}
                            label={`${t('masterVtds.gpsStatus', 'GPS Status')} *`}
                            onChange={formik.handleChange}
                          >
                            <MenuItem value="">{t('masterVtds.selectStatus', '— Select Status —')}</MenuItem>
                            {GPS_STATUS_OPTIONS.map((v) => (
                              <MenuItem key={v.key} value={v.value}>
                                {v.key}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          label={`${t('masterVtds.imeiNo', 'IMEI Number')} *`}
                          name="imei_no"
                          type="number"
                          value={formik.values.imei_no}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          fullWidth
                          error={formik.touched.imei_no && !!formik.errors.imei_no}
                          helperText={formik.touched.imei_no && formik.errors.imei_no}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <FormControl fullWidth error={formik.touched.gps_vendor && !!formik.errors.gps_vendor}>
                          <InputLabel>{`${t('masterVtds.gpsVendor', 'GPS Vendor')} *`}</InputLabel>
                          <MuiSelect
                            name="gps_vendor"
                            value={formik.values.gps_vendor}
                            label={`${t('masterVtds.gpsVendor', 'GPS Vendor')} *`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <MenuItem value="">{t('masterVtds.selectVendor', '— Select Vendor —')}</MenuItem>
                            {VENDOR_OPTIONS.map((v) => (
                              <MenuItem key={v} value={v}>
                                {v}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <TextField
                          label={`${t('masterVtds.port', 'Port')} *`}
                          name="port"
                          type="number"
                          value={formik.values.port}
                          onChange={formik.handleChange}
                          fullWidth
                          error={formik.touched.port && !!formik.errors.port}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <FormControlLabel
                    control={
                      <Switch checked={formik.values.blacklist} onChange={(e) => formik.setFieldValue('blacklist', e.target.checked)} />
                    }
                    label={t('masterVtds.blacklist', 'Blacklist')}
                    sx={{ mt: 1 }}
                  />

                  <TextField
                    label={t('general.description', 'Description')}
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 1 }}
                  />
                </Box>

                {/* Bagian 2: Integration */}
                <Box>
                  <SectionHeader title={t('masterVtds.integration', 'Integration')} />
                  <Grid container spacing={2}>
                    <Grid size={{ sm: 12, md: 12 }}>
                      <TextField
                        label={t('masterVtds.integrationVendor', 'Integration Vendor')}
                        name="integration_vendor"
                        value={formik.values.integration_vendor}
                        onChange={formik.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <TextField
                        label={t('masterVtds.integrationImei', 'Integration IMEI')}
                        name="integration_imei"
                        value={formik.values.integration_imei}
                        onChange={formik.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <TextField
                        label={t('masterVtds.maximumAllowData', 'Maximum Allow Data')}
                        name="maximum_allow_data"
                        type="number"
                        value={formik.values.maximum_allow_data}
                        onChange={formik.handleChange}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ sm: 12, md: 6 }}>
                <Box>
                  <SectionHeader title={t('masterVtds.sensorsIo', 'Sensors & I/O')} />
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormControl fullWidth key={`din${num}`}>
                          <InputLabel>{`${t('masterVtds.digitalInputNumber', 'Digital Input Number')} ${num}`}</InputLabel>
                          <MuiSelect
                            name={`din${num}_desc`}
                            value={formik.values[`din${num}_desc`]}
                            label={`${t('masterVtds.digitalInputNumber', 'Digital Input Number')} ${num}`}
                            onChange={formik.handleChange}
                          >
                            <MenuItem value="">{t('general.none', '— None —')}</MenuItem>
                            {DIN_OPTIONS.map((v) => (
                              <MenuItem key={v} value={v}>
                                {v}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      </Grid>
                    ))}
                    <Grid size={{ sm: 12, md: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>{t('masterVtds.fuel', 'Fuel')}</InputLabel>
                        <MuiSelect
                          name="fuel_desc"
                          value={formik.values.fuel_desc}
                          label={t('masterVtds.fuel', 'Fuel')}
                          onChange={(e) => {
                            const val = e.target.value;
                            formik.setFieldValue('fuel_desc', val);
                            if (val === 'AIN') {
                              formik.setFieldValue('have_ain', true);
                            } else {
                              formik.setFieldValue('have_ain', false);
                              formik.setFieldValue('ain', '');
                            }
                          }}
                        >
                          <MenuItem value="">{t('general.none', '— None —')}</MenuItem>
                          {FUEL_OPTIONS.map((v) => (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                    </Grid>

                    <Grid container spacing={2} size={{ sm: 12, md: 12, xs: 12 }}>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormControlLabel
                          control={<Switch checked={formik.values.have_ain} disabled />}
                          label={t('masterVtds.haveAinQ', 'Have AIN ?')}
                        />
                      </Grid>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormControl fullWidth disabled={formik.values.fuel_desc !== 'AIN'}>
                          <InputLabel>{t('masterVtds.ainValue', 'AIN Value')}</InputLabel>
                          <MuiSelect
                            name="ain"
                            value={formik.values.ain}
                            label={t('masterVtds.ainValue', 'AIN Value')}
                            onChange={formik.handleChange}
                          >
                            <MenuItem value="">{t('general.none', '— None —')}</MenuItem>
                            {AIN_OPTIONS.map((v) => (
                              <MenuItem key={v} value={v}>
                                {v}
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} size={{ sm: 12, md: 12, xs: 12 }}>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.eco_driver_supported}
                              onChange={(e) => formik.setFieldValue('eco_driver_supported', e.target.checked)}
                            />
                          }
                          label={t('masterVtds.ecoDriveSupportedQ', 'Eco Drive Supported?')}
                        />
                      </Grid>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.buzzer_installed}
                              onChange={(e) => formik.setFieldValue('buzzer_installed', e.target.checked)}
                            />
                          }
                          label={t('masterVtds.buzzerInstalledQ', 'Buzzer Installed?')}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} size={{ sm: 12, md: 12, xs: 12 }} sx={{ mb: 2 }}>
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.is_ev}
                              onChange={(e) => {
                                const isEv = e.target.checked;
                                formik.setFieldValue('is_ev', isEv);
                                if (isEv) {
                                  formik.setFieldValue('stick_fuel_sensor', false);
                                  formik.setFieldValue('canbus_fuel_sensor', false);
                                }
                              }}
                            />
                          }
                          label={t('masterVtds.isEvQ', 'Is Electric Vehicle (EV)?')}
                        />
                      </Grid>
                      {formik.values.is_ev && (
                        <Grid size={{ sm: 12, md: 6 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{t('masterVtds.distanceUnit', 'Distance Unit')}</InputLabel>
                            <MuiSelect
                              name="dist_in"
                              value={formik.values.dist_in}
                              onChange={formik.handleChange}
                              label={t('masterVtds.distanceUnit', 'Distance Unit')}
                            >
                              <MenuItem value="km">{t('masterVtds.km', 'Kilometer (km)')}</MenuItem>
                              <MenuItem value="m">{t('masterVtds.meter', 'Meter (m)')}</MenuItem>
                            </MuiSelect>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>

                    <Grid container spacing={2} size={{ sm: 12, md: 12, xs: 12 }}>
                      <Grid size={{ sm: 12, md: 3 }}>
                        <Typography variant="body1" fontWeight="bold" color={formik.values.is_ev ? 'text.disabled' : 'text.primary'}>
                          {t('masterVtds.fuelReportCalcBy', 'Fuel Report Calculation by:')}
                        </Typography>
                      </Grid>
                      <Grid size={{ sm: 12, md: 4 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.stick_fuel_sensor}
                              onChange={(e) => formik.setFieldValue('stick_fuel_sensor', e.target.checked)}
                              disabled={formik.values.is_ev}
                            />
                          }
                          label={t('masterVtds.stickFuelSensor', 'Stick Fuel Sensor')}
                        />
                      </Grid>
                      <Grid size={{ sm: 12, md: 5 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.canbus_fuel_sensor}
                              onChange={(e) => formik.setFieldValue('canbus_fuel_sensor', e.target.checked)}
                              disabled={formik.values.is_ev}
                            />
                          }
                          label={t('masterVtds.canbusFuelSensor', 'Canbus Fuel Sensor')}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <Grid sx={{ mb: 4, mt: 4 }}>
              <SectionHeader title={t('masterVtds.purchaseSalesInsurance', 'Purchase, Sales & Insurance')} />
              <Grid container spacing={2}>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.purchaseFrom', 'Purchase Form')}
                    name="purchase_form"
                    value={formik.values.purchase_form}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.purchaseDate', 'Purchase Date')}
                    name="purchase_date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.purchase_date}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.sellingPrice', 'Selling Price')}
                    value={formatRupiah(formik.values.selling_price)}
                    onChange={(e) => handleCurrencyChange(e, 'selling_price')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.sellingDate', 'Selling Date')}
                    name="selling_date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.selling_date}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.purchasePrice', 'Purchase Price')}
                    value={formatRupiah(formik.values.purchase_price)}
                    onChange={(e) => handleCurrencyChange(e, 'purchase_price')}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.warrantyDate', 'Warranty Date')}
                    name="warranty_date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.warranty_date}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.insuranceNo', 'Insurance Number')}
                    name="insurance_np"
                    value={formik.values.insurance_np}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                  <TextField
                    label={t('masterVtds.insuranceDate', 'Insurance Date')}
                    name="insurance_date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formik.values.insurance_date}
                    onChange={formik.handleChange}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterVtdsAddEdit;
